import { TextField } from "@mui/material";
import lodash from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { Grid } from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./SceneSettings.module.css";

interface GridFieldsProps {
  /** The grid as it currently stands. */
  value: Grid;
  /**
   * Called with the edited grid whenever the inputs parse to something
   * different from {@link GridFieldsProps.value}.
   *
   * Should be stable across renders — it is an effect dependency, and an inline
   * arrow makes the effect re-run on every render. Re-running is harmless (the
   * write is guarded by an equality check) but pointless.
   */
  onChange: (grid: Grid) => void;
}

/**
 * The floor grid's colour, cell size and height, as plain inputs.
 *
 * Presentational: it holds the half-typed text of each field and nothing else,
 * so both a panel — whose grid lives in the visualization store, behind a
 * `panelId` and a dispatch — and the ingest wizard — whose scene is local React
 * state with no panel anywhere — can edit a grid through the same controls. The
 * two used to be one component wired straight to the store, which is why the
 * wizard could not reuse it at all.
 *
 * Text state is seeded from `value` once and then owned here. A field the user
 * is midway through typing must not be rewritten under them by the value
 * flowing back in — "0." is not yet a number, and re-deriving it as "0" moves
 * the caret. The consequence is that a `value` changed by anything *other* than
 * these inputs is not picked up; that is what mounting fresh (a re-opened
 * dialog, a re-selected panel) is for.
 */
export function GridFields({ value, onChange }: GridFieldsProps) {
  const [rgbColor, setRGBColor] = React.useState(() => {
    // Color picker doesn't manage RGBA, only RGB.
    return value.color.substring(0, 7);
  });
  const [scale, setScale] = React.useState(`${value.scale}`);
  const [verticalOffset, setVerticalOffset] = React.useState(
    `${value.verticalOffset ?? ""}`,
  );

  const [scaleError, setScaleError] = React.useState("");
  const [verticalOffsetError, setVerticalOffsetError] = React.useState("");

  React.useEffect(
    function onSettingChanged() {
      setScaleError("");
      const parsedScale = scale === "" ? undefined : Number.parseFloat(scale);
      if (parsedScale === undefined) {
        // noop
      } else if (!Number.isFinite(parsedScale)) {
        setScaleError("Scale must be a number");
        return;
      } else if (parsedScale < 0) {
        setScaleError("Scale must be positive");
        return;
      }
      setVerticalOffsetError("");
      const parsedVerticalOffset =
        verticalOffset === "" ? undefined : Number.parseFloat(verticalOffset);
      if (
        parsedVerticalOffset !== undefined &&
        Number.isNaN(parsedVerticalOffset)
      ) {
        setVerticalOffsetError("Vertical offset must be a number");
        return;
      }
      const updatedGrid: Grid = {
        color: `${rgbColor}88`, // Add alpha back
        scale: parsedScale,
        verticalOffset: parsedVerticalOffset,
      };
      if (!lodash.isEqual(value, updatedGrid)) {
        onChange(updatedGrid);
      }
    },
    [rgbColor, scale, verticalOffset, value, onChange],
  );

  return (
    <div className={styles.container}>
      <RobotoTypography variant="body2" className={styles.sectionLabel}>
        Grid Settings
      </RobotoTypography>
      <TextField
        label="Color"
        type="color"
        size="small"
        fullWidth
        value={rgbColor}
        onChange={(e) => setRGBColor(e.target.value)}
        className={styles.field}
        InputLabelProps={{ className: styles.input }}
        InputProps={{ className: styles.input }}
        inputProps={{ className: styles.input }}
      />
      <div className={`${styles.inputContainer} ${styles.field}`}>
        <TextField
          label="Scale"
          type="number"
          size="small"
          autoComplete="off"
          value={scale}
          onChange={(e) => setScale(e.target.value)}
          error={!!scaleError}
          helperText={scaleError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          InputProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Vertical Offset"
          type="number"
          size="small"
          autoComplete="off"
          value={verticalOffset}
          onChange={(e) => setVerticalOffset(e.target.value)}
          error={!!verticalOffsetError}
          helperText={verticalOffsetError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          InputProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
      </div>
    </div>
  );
}
